/** Whether a request window covers a new booking request or a guest's change request. */
export type ReservationRequestWindowKind = 'request' | 'change';

/** How a request window closed. */
export type ReservationRequestWindowResolution =
  | 'accepted'
  | 'rejected'
  | 'declined'
  | 'cancelled'
  | 'expired'
  | 'expired_night'
  | 'closed';

/**
 * One request-to-book response cycle: the host's 24h window, the support alert, the guest's
 * one-time extension and the host's "why did you miss it" answer. All times are epoch seconds.
 */
export interface IReservationRequestWindow {
  id?: number;
  reservationId: number;
  kind: ReservationRequestWindowKind;
  requestedAt: number;
  responseDeadline: number;
  supportAlertSentAt?: number | null;
  hostMissedSentAt?: number | null;
  graceStartedAt?: number | null;
  graceDeadline?: number | null;
  guestExtendedAt?: number | null;
  hostExtensionNoticeSentAt?: number | null;
  adminExtensionCount?: number;
  adminExtendedAt?: number | null;
  adminExtendedById?: number | null;
  adminExtensionNote?: string | null;
  hostMissedReason?: string | null;
  hostMissedDetails?: string | null;
  hostMissedRespondedAt?: number | null;
  resolvedAt?: number | null;
  resolution?: ReservationRequestWindowResolution | null;
  created?: number;
}

/** Reasons a host can give for missing a request's response window. */
export const HOST_MISSED_REQUEST_REASONS: { key: string; label: string }[] = [
  { key: 'missed_notification', label: "I didn't see the notification" },
  { key: 'traveling_or_busy', label: 'I was traveling or busy' },
  { key: 'calendar_out_of_date', label: 'My calendar was out of date' },
  { key: 'not_a_fit', label: "The request wasn't a good fit" },
  { key: 'no_longer_hosting', label: "I'm no longer hosting" },
  { key: 'technical_problem', label: 'I had a technical problem' },
  { key: 'other', label: 'Other' },
];

/** What the public "extend your request" page shows a guest. */
export interface IRequestExtendView {
  state: 'can_extend' | 'already_extended' | 'resolved' | 'expired';
  isChange: boolean;
  listingName?: string;
  siteName?: string;
  checkIn?: string;
  checkOut?: string;
  hostFirstName?: string;
  /** Last moment the guest can extend (epoch seconds). */
  graceDeadline?: number | null;
  /** Host's response deadline: after extending, or what it would become (epoch seconds). */
  responseDeadline?: number;
  /** The request also expires when its first night ends (epoch seconds). */
  nightDeadline?: number | null;
}

/** What the public "why did you miss this request" page shows a host. */
export interface IHostRequestFeedbackView {
  isChange: boolean;
  listingName?: string;
  siteName?: string;
  checkIn?: string;
  checkOut?: string;
  guestFirstName?: string;
  reasons: { key: string; label: string }[];
  submitted: boolean;
  reason?: string | null;
  details?: string | null;
  /** The request is still open, so the host can still accept it. */
  stillPending: boolean;
  reservationUrl?: string;
}
